/* Import groupé de copies scannées (NAI-222, plan v7 —
   https://html.naia-edu.app/plan-import-groupe-copies-nai-222, écran
   https://html.naia-edu.app/plan-recap-import-groupe-copies-nai222).

   Phases : form (choix du PDF) -> uploading -> processing (job de fond,
   polling local — voir use-bulk-import-job.ts, pas le contexte global
   spec 13 : jobKind/champs différents, pas d'endpoint "jobs actifs"
   équivalent) -> recap (revue prof, cœur de l'écran) -> error.

   Le récap ne ferme jamais tout seul : le prof peut revenir plus tard sur
   une copie difficile (v7 §7, "jamais de choix silencieux") — validated_at
   marque une copie candidate comme traitée, les autres restent visibles. */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dialog } from '@base-ui/react';
import {
  CaretLeft,
  CaretRight,
  CheckCircle,
  CircleNotch,
  Images,
  Paperclip,
  Question,
  Warning,
  X,
} from '@phosphor-icons/react';
import { api } from '../lib/api';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBulkImportJob } from '../hooks/use-bulk-import-job';
import { useBulkImportJobs } from '../hooks/use-bulk-import-jobs';
import { runWithConcurrencyLimit } from '../lib/concurrency';

/* « Valider toutes les copies » (NAI-218) : un Promise.all non borné sur 21
 * candidats envoyait 21 téléchargements/rendus de PDF en simultané côté API,
 * provoquant des "Failed to fetch" réseau. 4 reste rapide pour les petits
 * lots (1-5) tout en bornant la charge sur les gros. */
const VALIDATE_ALL_CONCURRENCY = 4;

export interface ExamCopyBulkImportModalProps {
  sessionId: string;
  classroomId: string;
  onClose: () => void;
  /** Appelé à la fermeture si au moins une copie a été validée — le parent
   *  recharge le détail de session (nouvelles exam_copies apparues). */
  onValidated: () => void;
  /** Ouvre directement sur le récap d'un import déjà terminé (bouton « Voir
   *  les copies importées ») — saute form/uploading/processing, ne déclenche
   *  jamais d'upload ni de nouvelle analyse. */
  openToRecap?: boolean;
  /** Rouvre sur un import déjà en cours (bouton « Import en cours… »,
   *  NAI-218) — reprend le polling existant (useBulkImportJob) sans
   *  relancer d'upload ni une nouvelle analyse. */
  openToProcessing?: boolean;
}

type Phase = 'form' | 'uploading' | 'processing' | 'loading-recap' | 'recap' | 'error';

type CandidateStatus = 'attribuee' | 'ambigue' | 'a_attribuer';

interface PageRef {
  page_index: number;
  logical_page: number;
}

interface GroupedPageRef {
  page_index: number;
  logical_pages: number[];
}

function groupPageRefs(pageRefs: PageRef[]): GroupedPageRef[] {
  const order: number[] = [];
  const byPageIndex = new Map<number, number[]>();
  for (const ref of pageRefs) {
    const existing = byPageIndex.get(ref.page_index);
    if (existing) {
      existing.push(ref.logical_page);
    } else {
      byPageIndex.set(ref.page_index, [ref.logical_page]);
      order.push(ref.page_index);
    }
  }
  return order.map((page_index) => ({ page_index, logical_pages: byPageIndex.get(page_index)! }));
}

function formatLogicalPagesLabel(logicalPages: number[]): string {
  return logicalPages.length > 1 ? `Pages ${logicalPages.join(' et ')}` : `Page ${logicalPages[0]}`;
}

interface Candidate {
  id: string;
  sequence_no: number;
  status: CandidateStatus;
  proposed_classroom_student_id: string | null;
  proposed_student_display_name: string | null;
  match_confidence: number | null;
  confirmed_classroom_student_id: string | null;
  validated_at: string | null;
  exam_copy_id: string | null;
  page_refs: PageRef[];
  uncertainty_reason: string | null;
}

interface Student {
  id: string;
  first_name: string;
  last_name: string;
}

const STEP_LABEL: Record<string, string> = {
  generating: 'Analyse du document en cours…',
  saving: 'Enregistrement des copies détectées…',
  done: 'Terminé',
};

/* Mapping systématique failureKind → message FR actionnable — jamais le
 * job.error brut (qui peut porter une erreur Vertex technique telle quelle,
 * NAI-218). Même pattern que FAILURE_KIND_LABEL de exam-import-progress.tsx. */
const FAILURE_KIND_LABEL: Record<string, string> = {
  appel_modele: "L'appel au modèle d'analyse a échoué. Réessaie dans quelques instants.",
  json_invalide: "La réponse du modèle n'était pas exploitable. Réessaie l'import.",
  invariant_page_partagee: 'Une page du PDF semble appartenir à deux copies différentes. Vérifie le document source.',
  document_sans_session: "Ce document n'est plus rattaché à une session d'examen valide.",
  persistance: "L'enregistrement des copies détectées a échoué. Réessaie l'import.",
  interrompu: "Le traitement a été interrompu avant la fin. Réessaie l'import.",
  inconnu: "Une erreur inattendue est survenue pendant l'import.",
};

function failureMessage(failureKind: string | null): string {
  return (failureKind && FAILURE_KIND_LABEL[failureKind]) || FAILURE_KIND_LABEL['inconnu']!;
}

const STATUS_META: Record<CandidateStatus, { label: string; badge: 'secondary' | 'outline' | 'destructive'; icon: typeof CheckCircle }> = {
  attribuee: { label: 'Attribuée', badge: 'secondary', icon: CheckCircle },
  ambigue: { label: 'Ambiguë', badge: 'outline', icon: Warning },
  a_attribuer: { label: 'À attribuer', badge: 'destructive', icon: Question },
};

const MAX_PREVIEW_PAGES = 4;

const errorMessage = (e: unknown) =>
  e instanceof TypeError
    ? "L'envoi n'a pas abouti — vérifiez votre connexion, puis réessayez."
    : e instanceof Error
      ? e.message
      : String(e);

function PageLightbox({
  candidateId,
  pageGroups,
  index,
  images,
  onLoaded,
  onNavigate,
  onClose,
}: {
  candidateId: string;
  pageGroups: GroupedPageRef[];
  index: number;
  images: Record<number, string>;
  onLoaded: (pageIndex: number, dataUrl: string) => void;
  onNavigate: (index: number) => void;
  onClose: () => void;
}) {
  const group = pageGroups[index];
  const src = group ? images[group.page_index] : undefined;
  const label = group ? formatLogicalPagesLabel(group.logical_pages) : '';

  useEffect(() => {
    if (!group || src) return;
    let cancelled = false;
    api.exam.bulkImportCandidatePreview
      .query({ candidateId, pageIndex: group.page_index })
      .then((r) => {
        if (cancelled) return;
        onLoaded(group.page_index, `data:${r.content_type};base64,${r.base64}`);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [candidateId, group, src, onLoaded]);

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) onClose(); }}>
      <Dialog.Portal>
        <Dialog.Viewport className="fixed inset-0 z-[1200] flex items-center justify-center p-4 bg-[rgba(11,42,74,0.7)]">
          <Dialog.Popup
            className="relative flex flex-col items-center gap-3 w-full max-w-[min(90vw,720px)] max-h-[90vh]"
            aria-label={label}
          >
            <Button
              variant="ghost"
              size="icon"
              className="absolute -top-2 -right-2 z-10 bg-card"
              onClick={onClose}
              aria-label="Fermer l'aperçu"
            >
              <X size={18} />
            </Button>
            <div className="flex items-center justify-center w-full min-h-[240px] bg-card rounded-card overflow-hidden">
              {src ? (
                <img src={src} alt={label} className="max-w-full max-h-[75vh] object-contain" />
              ) : (
                <CircleNotch size={32} className="animate-spin text-muted-foreground" />
              )}
            </div>
            <div className="flex items-center justify-between w-full">
              <Button
                variant="secondary"
                size="icon"
                className="min-w-11 min-h-11"
                disabled={index === 0}
                onClick={() => onNavigate(index - 1)}
                aria-label="Page précédente"
              >
                <CaretLeft size={20} weight="bold" />
              </Button>
              <span className="text-[13px] text-white">
                {label} — {index + 1}/{pageGroups.length}
              </span>
              <Button
                variant="secondary"
                size="icon"
                className="min-w-11 min-h-11"
                disabled={index === pageGroups.length - 1}
                onClick={() => onNavigate(index + 1)}
                aria-label="Page suivante"
              >
                <CaretRight size={20} weight="bold" />
              </Button>
            </div>
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function CandidatePreview({ candidateId, pageRefs }: { candidateId: string; pageRefs: PageRef[] }) {
  const [images, setImages] = useState<Record<number, string>>({});
  const [open, setOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const groups = useMemo(() => groupPageRefs(pageRefs), [pageRefs]);
  const shown = groups.slice(0, MAX_PREVIEW_PAGES);

  const loadImage = useCallback((pageIndex: number, dataUrl: string) => {
    setImages((prev) => ({ ...prev, [pageIndex]: dataUrl }));
  }, []);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    shown.forEach((group) => {
      if (images[group.page_index]) return;
      api.exam.bulkImportCandidatePreview
        .query({ candidateId, pageIndex: group.page_index })
        .then((r) => {
          if (cancelled) return;
          loadImage(group.page_index, `data:${r.content_type};base64,${r.base64}`);
        })
        .catch(() => {});
    });
    return () => { cancelled = true; };
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- shown est dérivé de pageRefs, images se remplit progressivement */
  }, [open, candidateId]);

  return (
    <div className="flex flex-col gap-2">
      <Button variant="ghost" size="sm" onClick={() => setOpen((v) => !v)}>
        <Images size={15} weight="bold" className="mr-1.5" />
        {open ? 'Masquer les pages' : 'Voir les pages'}
      </Button>
      {open && (
        <div className="flex gap-2 flex-wrap">
          {shown.map((group, i) => (
            <button
              key={group.page_index}
              type="button"
              onClick={() => setLightboxIndex(i)}
              className="w-[64px] h-[84px] rounded-md border border-border bg-muted overflow-hidden relative flex items-center justify-center cursor-zoom-in"
              aria-label={`Agrandir la ${formatLogicalPagesLabel(group.logical_pages).toLowerCase()}`}
            >
              {images[group.page_index] ? (
                <img src={images[group.page_index]} alt={formatLogicalPagesLabel(group.logical_pages)} className="w-full h-full object-cover" />
              ) : (
                <CircleNotch size={18} className="animate-spin text-muted-foreground" />
              )}
              <span className="absolute bottom-0.5 right-1 text-[9px] bg-black/50 text-white px-1 rounded">
                {group.logical_pages.join(', ')}
              </span>
            </button>
          ))}
          {groups.length > MAX_PREVIEW_PAGES && (
            <div className="w-[64px] h-[84px] rounded-md border border-border bg-muted flex items-center justify-center text-[11px] text-muted-foreground">
              +{groups.length - MAX_PREVIEW_PAGES}
            </div>
          )}
        </div>
      )}
      {lightboxIndex !== null && (
        <PageLightbox
          candidateId={candidateId}
          pageGroups={groups}
          index={lightboxIndex}
          images={images}
          onLoaded={loadImage}
          onNavigate={setLightboxIndex}
          onClose={() => setLightboxIndex(null)}
        />
      )}
    </div>
  );
}

function CandidateCard({
  candidate,
  roster,
  onChanged,
  externalError,
}: {
  candidate: Candidate;
  roster: Student[];
  onChanged: () => void;
  externalError?: string | null;
}) {
  const meta = STATUS_META[candidate.status];
  const Icon = meta.icon;
  const [selected, setSelected] = useState(
    candidate.confirmed_classroom_student_id ?? candidate.proposed_classroom_student_id ?? '',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const validated = candidate.validated_at !== null;
  const dirty = selected !== '' && selected !== (candidate.confirmed_classroom_student_id ?? candidate.proposed_classroom_student_id ?? '');
  const assignedStudentId = candidate.confirmed_classroom_student_id ?? candidate.proposed_classroom_student_id;
  const shownError = error ?? externalError ?? null;

  /* Passé explicitement à <Select items=…> : le composant @base-ui/react
   * résout le libellé affiché (SelectValue) depuis les <SelectItem> montés
   * dans le popup, qui ne le sont QUE quand le popup a déjà été ouvert
   * (pas de keepMounted). Sans ce prop, un élève confirmé sans réouverture
   * du popup s'affiche par son id brut au lieu de son nom (NAI-218). */
  const selectItems = useMemo(() => {
    const map: Record<string, string> = {};
    if (candidate.proposed_student_display_name && candidate.proposed_classroom_student_id) {
      map[candidate.proposed_classroom_student_id] = candidate.proposed_student_display_name;
    }
    for (const s of roster) {
      if (s.id === candidate.proposed_classroom_student_id) continue;
      map[s.id] = `${s.first_name} ${s.last_name}`;
    }
    return map;
  }, [candidate.proposed_classroom_student_id, candidate.proposed_student_display_name, roster]);

  const confirm = async () => {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await api.exam.confirmBulkImportCandidateStudent.mutate({ candidateId: candidate.id, classroomStudentId: selected });
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const validate = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.exam.validateBulkImportCandidate.mutate({ candidateId: candidate.id });
      onChanged();
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-2 p-3.5 bg-card border-[1.5px] border-border rounded-card">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[12px] text-muted-foreground font-semibold">Copie #{candidate.sequence_no}</span>
        <Badge variant={meta.badge}>
          <Icon size={12} weight="bold" className="mr-1" />
          {meta.label}
        </Badge>
        {validated && <Badge variant="secondary">Validée</Badge>}
        {candidate.match_confidence !== null && !validated && (
          <span className="text-[12px] text-muted-foreground">{Math.round(candidate.match_confidence * 100)} % de confiance</span>
        )}
      </div>

      {candidate.uncertainty_reason && (
        <p className="m-0 flex items-start gap-1.5 text-[12.5px] leading-[1.45] text-[var(--naia-text-primary)] bg-[var(--naia-warn-bg)] rounded-[var(--naia-r-card)] px-2.5 py-2">
          <Warning size={15} weight="fill" className="shrink-0 mt-0.5 text-[var(--naia-tertiary)]" />
          {candidate.uncertainty_reason}
        </p>
      )}

      {!validated && !candidate.uncertainty_reason && !assignedStudentId && (
        <p className="m-0 flex items-start gap-1.5 text-[12.5px] leading-[1.45] text-[var(--naia-text-primary)] bg-[var(--naia-warn-bg)] rounded-[var(--naia-r-card)] px-2.5 py-2">
          <Warning size={15} weight="fill" className="shrink-0 mt-0.5 text-[var(--naia-tertiary)]" />
          Aucun élève sélectionné — choisis un élève avant de valider.
        </p>
      )}

      <CandidatePreview candidateId={candidate.id} pageRefs={candidate.page_refs} />

      {!validated && (
        <div className="flex items-center gap-2 flex-wrap">
          <Select items={selectItems} value={selected || null} onValueChange={(v) => setSelected(v ?? '')}>
            <SelectTrigger className="!w-auto !h-9 text-[12.5px] min-w-[220px]">
              <SelectValue placeholder="— choisir un élève —" />
            </SelectTrigger>
            <SelectContent>
              {candidate.proposed_student_display_name && candidate.proposed_classroom_student_id && (
                <SelectItem value={candidate.proposed_classroom_student_id}>
                  {candidate.proposed_student_display_name} (proposé{candidate.match_confidence !== null ? `, ${Math.round(candidate.match_confidence * 100)} %` : ''})
                </SelectItem>
              )}
              {roster
                .filter((s) => s.id !== candidate.proposed_classroom_student_id)
                .map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.first_name} {s.last_name}</SelectItem>
                ))}
            </SelectContent>
          </Select>
          {dirty && (
            <Button variant="ghost" size="sm" disabled={busy} onClick={() => void confirm()}>
              Confirmer cet élève
            </Button>
          )}
        </div>
      )}

      {shownError && <Alert variant="destructive">{shownError}</Alert>}

      {!validated && (
        <div className="flex justify-end">
          <Button disabled={busy || !selected} onClick={() => void validate()}>
            {busy ? 'Validation…' : 'Valider cette copie'}
          </Button>
        </div>
      )}
    </div>
  );
}

export default function ExamCopyBulkImportModal({ sessionId, classroomId, onClose, onValidated, openToRecap, openToProcessing }: ExamCopyBulkImportModalProps) {
  const [phase, setPhase] = useState<Phase>(openToRecap ? 'loading-recap' : openToProcessing ? 'processing' : 'form');
  const [file, setFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<Candidate[]>([]);
  const [roster, setRoster] = useState<Student[]>([]);
  const [anyValidated, setAnyValidated] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [candidateErrors, setCandidateErrors] = useState<Record<string, string>>({});

  const { job } = useBulkImportJob(sessionId, phase === 'processing');

  /* Suivi global (barre de progression + toast, montés dans AppShell) — la
     fermeture de la fenêtre pendant le traitement ne doit rien interrompre :
     le job continue côté serveur, ce contexte en reprend le suivi (NAI-222,
     GO Ismaël 09/10). Même pattern que ExamImportModal (exam-import-modal.tsx). */
  const { trackJob, releaseJob } = useBulkImportJobs();

  useEffect(() => {
    if (phase !== 'processing') return;
    trackJob(sessionId, { claim: true });
  }, [phase, sessionId, trackJob]);

  useEffect(() => () => releaseJob(sessionId), [sessionId, releaseJob]);

  const loadRecap = useCallback(async () => {
    const [c, r] = await Promise.all([
      api.exam.bulkImportCandidates.query({ sessionId }) as Promise<Candidate[]>,
      api.teacher.classroomStudents.query({ classroomId }) as Promise<Student[]>,
    ]);
    setCandidates(c);
    setRoster(r);
    setPhase('recap');
  }, [sessionId, classroomId]);

  useEffect(() => {
    if (!openToRecap) return;
    void loadRecap();
    /* eslint-disable-next-line react-hooks/exhaustive-deps -- une seule fois au montage, openToRecap ne change pas en cours de vie du modal */
  }, []);

  useEffect(() => {
    if (phase !== 'processing' || !job) return;
    if (job.status === 'succeeded') void loadRecap();
    else if (job.status === 'failed') {
      console.error(`[exam-copy-bulk-import] job=${job.id} en échec (${job.failureKind ?? 'inconnu'}) —`, job.error);
      setError(failureMessage(job.failureKind));
      setPhase('error');
    }
  }, [phase, job, loadRecap]);

  const startImport = async () => {
    if (!file) return;
    setPhase('uploading');
    setError(null);
    try {
      const { path, signed_url } = await api.exam.bulkImportUploadUrl.mutate({ sessionId });
      const put = await fetch(signed_url, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/pdf' },
      });
      if (!put.ok) throw new Error("L'envoi du fichier n'a pas abouti.");
      await api.exam.startBulkImport.mutate({ sessionId, storagePath: path, mimeType: file.type || 'application/pdf' });
      setPhase('processing');
    } catch (e) {
      setError(errorMessage(e));
      setPhase('error');
    }
  };

  const onCandidateChanged = (candidateId: string) => {
    setAnyValidated(true);
    setCandidateErrors((prev) => {
      if (!(candidateId in prev)) return prev;
      const next = { ...prev };
      delete next[candidateId];
      return next;
    });
    void loadRecap();
  };

  const readyCandidates = useMemo(
    () => candidates.filter((c) => !c.validated_at && (c.confirmed_classroom_student_id ?? c.proposed_classroom_student_id)),
    [candidates],
  );
  const allValidated = candidates.length > 0 && candidates.every((c) => c.validated_at !== null);

  const validateAll = async () => {
    if (readyCandidates.length === 0) return;
    setBulkBusy(true);
    setBulkProgress({ done: 0, total: readyCandidates.length });
    const nextErrors: Record<string, string> = {};
    let done = 0;
    await runWithConcurrencyLimit(readyCandidates, VALIDATE_ALL_CONCURRENCY, async (c) => {
      try {
        await api.exam.validateBulkImportCandidate.mutate({ candidateId: c.id });
      } catch (e) {
        nextErrors[c.id] = errorMessage(e);
      } finally {
        done += 1;
        setBulkProgress({ done, total: readyCandidates.length });
      }
    });
    setCandidateErrors((prev) => {
      const next = { ...prev };
      for (const c of readyCandidates) delete next[c.id];
      return { ...next, ...nextErrors };
    });
    setAnyValidated(true);
    setBulkBusy(false);
    setBulkProgress(null);
    await loadRecap();
  };

  const confirmAndCorrect = () => {
    onClose();
    onValidated();
  };

  const [pendingOpen, setPendingOpen] = useState(true);
  const [readyOpen, setReadyOpen] = useState(false);

  /* Les deux sections ("À vérifier" / "Attribuées automatiquement") sont
   * rendues comme un SEUL tableau d'enfants d'un unique parent DOM (voir
   * plus bas) : un candidat qui change de groupe (status attribuee) change
   * de position dans ce tableau mais jamais de parent, donc React le
   * réconcilie par key au lieu de le démonter — CandidatePreview garde son
   * cache d'images chargées. Deux <details> séparés (ancienne version)
   * sont deux parents DOM distincts : un changement de groupe y force un
   * démontage/remontage même à key identique (NAI-218). */
  const grouped = useMemo(() => {
    const pending = candidates.filter((c) => !c.validated_at && c.status !== 'attribuee');
    const readyOrDone = candidates.filter((c) => c.validated_at || c.status === 'attribuee');
    return { pending, readyOrDone };
  }, [candidates]);

  const close = () => {
    onClose();
    if (anyValidated) onValidated();
  };

  return (
    <Dialog.Root open onOpenChange={(o) => { if (!o) close(); }}>
      <Dialog.Portal>
        <Dialog.Viewport className="fixed inset-0 z-[1100] flex items-center justify-center p-4 bg-[rgba(11,42,74,0.4)]">
          <Dialog.Popup
            className="flex flex-col gap-3.5 w-full max-w-[720px] max-h-[90vh] overflow-y-auto bg-card rounded-card shadow-card p-5"
            aria-label="Import groupé de copies"
          >
            <div className="flex items-center justify-between">
              <h2 className="m-0 text-[18px] text-foreground">Import groupé de copies</h2>
              <Button variant="ghost" size="icon" onClick={close} aria-label="Fermer">
                <X size={18} />
              </Button>
            </div>

            {phase === 'form' && (
              <>
                <p className="m-0 text-[13.5px] text-muted-foreground">
                  Sélectionnez le PDF contenant les copies scannées de toute la classe pour cette session.
                </p>
                <label className="flex flex-col items-center gap-1.5 p-7 border-2 border-dashed border-border rounded-card text-muted-foreground text-center cursor-pointer hover:border-muted-foreground">
                  <Paperclip size={26} />
                  <span className="text-[14px]">{file ? file.name : 'Choisir un fichier PDF'}</span>
                  <input
                    type="file"
                    accept="application/pdf"
                    hidden
                    onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                  />
                </label>
                {error && <Alert variant="destructive">{error}</Alert>}
                <div className="flex justify-end">
                  <Button disabled={!file} onClick={() => void startImport()}>Lancer l'import</Button>
                </div>
              </>
            )}

            {(phase === 'uploading' || phase === 'processing' || phase === 'loading-recap') && (
              <div className="flex flex-col items-center gap-3 py-10 text-muted-foreground">
                <CircleNotch size={32} className="animate-spin" />
                <p className="m-0 text-[14px]">
                  {phase === 'uploading'
                    ? 'Envoi du fichier…'
                    : phase === 'loading-recap'
                      ? 'Chargement des copies importées…'
                      : STEP_LABEL[job?.step ?? 'generating']}
                </p>
              </div>
            )}

            {phase === 'error' && (
              <>
                <Alert variant="destructive">{error}</Alert>
                <div className="flex justify-end">
                  <Button variant="secondary" onClick={() => { setPhase('form'); setFile(null); setError(null); }}>
                    Réessayer
                  </Button>
                </div>
              </>
            )}

            {phase === 'recap' && (
              <div className="flex flex-col gap-2.5">
                {candidates.length > 0 && (
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <Button
                      variant="secondary"
                      disabled={bulkBusy || readyCandidates.length === 0}
                      onClick={() => void validateAll()}
                    >
                      {bulkBusy
                        ? `Validation ${bulkProgress?.done ?? 0}/${bulkProgress?.total ?? 0}…`
                        : `Valider toutes les copies (${readyCandidates.length})`}
                    </Button>
                    <Button disabled={!allValidated} onClick={confirmAndCorrect}>
                      Confirmer et passer à la correction
                    </Button>
                  </div>
                )}
                {grouped.pending.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setPendingOpen((v) => !v)}
                    className="flex items-center gap-1.5 font-bold text-[14px] cursor-pointer bg-transparent border-0 p-0 text-left"
                  >
                    <CaretRight size={14} weight="bold" className={pendingOpen ? 'rotate-90' : ''} />
                    À vérifier ({grouped.pending.length})
                  </button>
                )}
                {grouped.pending.map((c) => (
                  <div key={c.id} className={pendingOpen ? '' : 'hidden'}>
                    <CandidateCard
                      candidate={c}
                      roster={roster}
                      onChanged={() => onCandidateChanged(c.id)}
                      externalError={candidateErrors[c.id]}
                    />
                  </div>
                ))}
                {grouped.readyOrDone.length > 0 && (
                  <button
                    type="button"
                    onClick={() => setReadyOpen((v) => !v)}
                    className="flex items-center gap-1.5 font-bold text-[14px] cursor-pointer bg-transparent border-0 p-0 text-left mt-1.5"
                  >
                    <CaretRight size={14} weight="bold" className={readyOpen ? 'rotate-90' : ''} />
                    Attribuées automatiquement ({grouped.readyOrDone.length})
                  </button>
                )}
                {grouped.readyOrDone.map((c) => (
                  <div key={c.id} className={readyOpen ? '' : 'hidden'}>
                    <CandidateCard
                      candidate={c}
                      roster={roster}
                      onChanged={() => onCandidateChanged(c.id)}
                      externalError={candidateErrors[c.id]}
                    />
                  </div>
                ))}
                {candidates.length === 0 && (
                  <p className="text-center text-muted-foreground py-8">Aucune copie détectée dans ce document.</p>
                )}
              </div>
            )}
          </Dialog.Popup>
        </Dialog.Viewport>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
